import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  Euro as EuroIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const ViewSupplierModal = ({ 
  open, 
  onClose, 
  supplier,
  onEdit 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (!supplier) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'suspended': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Attivo';
      case 'inactive': return 'Inattivo';
      case 'suspended': return 'Sospeso';
      default: return status;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <span>{supplier.company_name}</span>
          </Box>
          <Chip 
            label={getStatusLabel(supplier.status)}
            color={getStatusColor(supplier.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Dati Aziendali */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Dati Aziendali
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Codice Fornitore</Typography>
                  <Typography fontWeight="bold">{supplier.supplier_code || 'N/D'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">P.IVA</Typography>
                  <Typography fontWeight="bold">{supplier.vat_number || 'N/D'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Ragione Sociale</Typography>
                  <Typography>{supplier.company_name}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Contatti */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Contatti
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {supplier.contact_person && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">{supplier.contact_person}</Typography>
                  </Box>
                )}
                {supplier.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{supplier.phone}</Typography>
                  </Box>
                )}
                {supplier.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{supplier.email}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Contratto */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Dati Contratto
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Data Inizio Contratto</Typography>
                  <Typography>{formatDate(supplier.contract_start_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Data Fine Contratto</Typography>
                  <Typography>{formatDate(supplier.contract_end_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Termini di Pagamento</Typography>
                  <Typography>{supplier.payment_terms || 'N/D'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EuroIcon color="primary" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Prezzo Corrente/L</Typography>
                      <Typography fontWeight="bold" color="primary">
                        {formatCurrency(supplier.current_price_per_liter)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Statistiche */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Statistiche</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Fatture Totali</Typography>
                  <Typography variant="h6">
                    {supplier.invoiceStats?.total || supplier.invoices?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Fatture Non Pagate</Typography>
                  <Typography variant="h6" color={supplier.invoiceStats?.unpaid > 0 ? 'error.main' : 'inherit'}>
                    {supplier.invoiceStats?.unpaid || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Totale Fatturato</Typography>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    {formatCurrency(supplier.invoiceStats?.totalAmount || 0)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Programmatori */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Programmatori ({supplier.planners?.length || 0})
              </Typography>
              
              {supplier.planners && supplier.planners.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Ruolo</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Telefono</TableCell>
                        <TableCell>Cellulare</TableCell>
                        <TableCell>Zone</TableCell>
                        <TableCell align="center">Principale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplier.planners.map((planner) => (
                        <TableRow key={planner.id}>
                          <TableCell fontWeight="bold">{planner.name}</TableCell>
                          <TableCell>{planner.role || 'N/D'}</TableCell>
                          <TableCell>{planner.email || 'N/D'}</TableCell>
                          <TableCell>{planner.phone || 'N/D'}</TableCell>
                          <TableCell>{planner.mobile || 'N/D'}</TableCell>
                          <TableCell>
                            {planner.coverage_areas ? (
                              Array.isArray(planner.coverage_areas) 
                                ? planner.coverage_areas.join(', ')
                                : planner.coverage_areas
                            ) : 'N/D'}
                          </TableCell>
                          <TableCell align="center">
                            {planner.is_primary && (
                              <Chip label="Sì" color="primary" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Nessun programmatore associato
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Storico Prezzi */}
          {supplier.priceHistory && supplier.priceHistory.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ReceiptIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="primary">
                    Storico Prezzi (ultimi 10)
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Prezzo/L</TableCell>
                        <TableCell>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplier.priceHistory.slice(0, 10).map((price, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{formatDate(price.date)}</TableCell>
                          <TableCell align="right" fontWeight="bold">
                            {formatCurrency(price.price)}
                          </TableCell>
                          <TableCell>{price.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* Note */}
          {supplier.notes && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Note</Typography>
                <Typography variant="body2">{supplier.notes}</Typography>
              </Paper>
            </Grid>
          )}

          {/* Metadati */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Creato il: {formatDate(supplier.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ultimo aggiornamento: {formatDate(supplier.updatedAt)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {onEdit && supplier.status !== 'inactive' && (
          <Button onClick={() => onEdit(supplier)} color="primary">
            Modifica
          </Button>
        )}
        <Button onClick={onClose} variant="contained">Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewSupplierModal;
